import React, { useEffect, useState } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';

export default function ContentListView() {
  const [data, setData] = useState({ rows: [], page: 1, limit: 10, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const totalPages = Math.ceil(data.total / data.limit);

  const fetchData = async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        'https://4e352fnqs7.execute-api.eu-central-1.amazonaws.com/uatt/do-action',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer 980c9cca-8fc2-11f0-8d33-02e13bb30071',
          },
          body: JSON.stringify({
            do_action: 'f_list_contents',
            data: { page, content_type: 'webscraped' },
          }),
        }
      );
      if (!response.ok) throw new Error(`API Error: ${response.status}`);
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) fetchData(newPage);
  };

  return (
    <div className="flex-grow flex flex-col min-h-[500px]">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-gray-300">Content List</h3>
        <button
          onClick={() => fetchData(1)}
          disabled={isLoading}
          className="bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold py-1 px-3 rounded-lg flex items-center gap-2"
        >
          <RefreshCw
            size={14}
            className={isLoading ? 'animate-spin' : ''}
          />{' '}
          Refresh
        </button>
      </div>
      <div className="bg-gray-900/50 rounded-lg flex-grow overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-400">
            <p>Error: {error}</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-700">
            {data.rows.map((item, index) => (
              <li key={item.id || `${item.title}-${index}`} className="p-4">
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-cyan-400 hover:text-cyan-300 hover:underline"
                  >
                    {item.title || 'No Title'}
                  </a>
                ) : (
                  <p className="font-semibold text-white">
                    {item.title || 'No Title'}
                  </p>
                )}
                <p className="text-sm text-gray-400">
                  {item.description || 'No description available.'}
                </p>
                <div className="text-xs text-gray-500 mt-2 flex gap-4 flex-wrap">
                  {item.created_at && (
                    <span>Pulled On: {new Date(item.created_at).toLocaleDateString()}</span>
                  )}
                  {item.priority_date && (
                    <span>Created: {new Date(item.priority_date).toLocaleString()}</span>
                  )}
                  {item.published_at && (
                    <span>Published: {new Date(item.published_at).toLocaleString()}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {!isLoading && data.total > 0 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
          <button
            onClick={() => handlePageChange(data.page - 1)}
            disabled={data.page <= 1}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg"
          >
            Previous
          </button>
          <span className="text-gray-400">
            Page {data.page} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(data.page + 1)}
            disabled={data.page >= totalPages}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

